import { useEffect, useCallback, useRef } from 'react';
import { X } from 'lucide-react';
import { StorageImage } from './StorageImage';

// 画像の拡大表示。閉じ方は 4 通り: 右上の×／画像の外側・画像のクリック／Esc／ブラウザの「戻る」。
// 「戻る」でアプリごと前のページへ抜けないよう、開いている間だけ履歴を 1 件積み、
// popstate（戻る）で閉じる。×・Esc・クリックで閉じるときは積んだ履歴を history.back() で戻す。
// 「積んだかどうか」は history.state ではなくこの変数で覚える。再読み込みやブラウザの進む・戻るで
// 前回の印が history.state に残っていても、それを自分の履歴と取り違えて history.back() でアプリの外へ
// 出てしまわないため。StrictMode の二重実行や表示中の画像の切り替えでは、積み済みなら積み直さない。
const ZOOM_STATE_KEY = '__imageZoom';
let zoomEntryPushed = false;

export function ImageZoomOverlay({ path, onClose }) {
  // 親がインライン関数を渡しても、リスナーの付け外しを繰り返さないよう ref 経由で呼ぶ。
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!path) return undefined;
    if (!zoomEntryPushed) {
      try {
        window.history.pushState({ [ZOOM_STATE_KEY]: true }, '');
        zoomEntryPushed = true;
      } catch { /* 履歴が使えない環境では×・Esc・クリックだけで閉じる */ }
    }
    const handlePop = () => { zoomEntryPushed = false; onCloseRef.current(); };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [path]);

  // 画面が拡大表示ごと外れた（ログアウトや画面の切り替え）ときは、積んだ履歴を戻す。
  // StrictMode の付け外しでは直後に付け直されるので、1 tick 待って外れたままのときだけ戻す。
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      setTimeout(() => {
        if (!mountedRef.current && zoomEntryPushed) {
          zoomEntryPushed = false;
          try { window.history.back(); } catch { /* 何もしない */ }
        }
      }, 0);
    };
  }, []);

  const close = useCallback(() => {
    // 履歴を戻すと popstate 経由で onClose が呼ばれる。積めていない環境では直接閉じる。
    if (zoomEntryPushed) window.history.back();
    else onCloseRef.current();
  }, []);

  useEffect(() => {
    if (!path) return undefined;
    const handleKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [path, close]);

  if (!path) return null;
  return (
    <div
      className="fixed inset-0 bg-black/80 z-[70] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="画像の拡大表示"
      onClick={close}
    >
      <StorageImage path={path} className="max-w-full max-h-full object-contain rounded-lg" alt="zoom" />
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); close(); }}
        className="absolute top-3 right-3 flex items-center gap-1 rounded-full bg-black/70 hover:bg-black/90 text-white px-3 py-2 text-sm font-bold border border-white/60 shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        aria-label="閉じる"
        style={{ top: 'max(0.75rem, env(safe-area-inset-top))' }}
      >
        <X className="w-5 h-5" aria-hidden="true" />
        閉じる
      </button>
    </div>
  );
}
